import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import FlagDisplay from '../components/FlagDisplay';
import { oaklandLanguages } from '../language/languages.js';
import '../css/pages/ProfessionalLanguagePage.css';
import '../css/global/index.css';
import '../css/components/FlagDisplay.css';

// Use Oakland-specific languages from centralized file
const languages = oaklandLanguages;

function ProfessionalLanguagePage() {
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hexagonPositions, setHexagonPositions] = useState([]);
  const navigate = useNavigate();
  const { changeLanguage } = useLanguage();
  const { theme } = useTheme();
  const { user, loading } = useAuth();

  // Generate random hexagon positions on component mount
  useEffect(() => {
    const generateRandomPositions = () => {
      const positions = [];
      for (let i = 0; i < 50; i++) {
        positions.push({
          top: Math.random() * 100, // Random top position (0-100%)
          left: Math.random() * 100, // Random left position (0-100%)
          delay: Math.random() * 2, // Random animation delay (0-2s)
          opacity: 0.03 + Math.random() * 0.05 // Random opacity (0.03-0.08)
        });
      }
      setHexagonPositions(positions);
    };

    generateRandomPositions();
  }, []);

  // Check if user is already logged in and redirect to fullchat
  useEffect(() => {
    if (!loading && user) {
      console.log('User already logged in, redirecting to fullchat...');
      navigate('/fullchat', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleLanguageSelect = async (language) => {
    setIsLoading(true);
    setSelectedLanguage(language);
    
    // Simulate AI processing
    await new Promise(resolve => setTimeout(resolve, 800));
    
    changeLanguage(language.code);
    
    // Navigate to login with smooth transition
    setTimeout(() => {
      navigate('/login');
    }, 500);
  };

  return (
    <div className="main-container" style={{ 
      background: "#111827",
      position: "relative",
      overflow: "hidden"
    }}>
      {/* Hexagonal Background Pattern */}
      <div className="hexagonal-background">
        <div className="hex-grid">
          {hexagonPositions.map((pos, i) => (
            <div 
              key={i} 
              className="hexagon" 
              style={{
                top: `${pos.top}%`,
                left: `${pos.left}%`,
                animationDelay: `${pos.delay}s`,
                opacity: pos.opacity
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Left side - Welcome section */}
      <div className="welcome-section">
        <div className="logo-section">
          <div className="logo-text" style={{
            fontSize: '28px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.02em'
          }}>Oakland AI</div>
        </div>
        
        <h1 className="main-heading">
          Smart help for<br />
          small businesses<br />
          in Oakland.
        </h1>
        <p className="sub-heading">
          Speak your language.<br />
          Get answers. No paperwork.
        </p>
        <p style={{ color: '#64748b', fontSize: '16px' }}>
          Choose your preferred language to get started
        </p>
      </div>

      {/* Right side - Language selection section */}
      <div className="login-section">
        <div className="login-container">
          <h2 className="login-title">
            {selectedLanguage ? `Welcome to ${selectedLanguage.native}` : 'Choose Your Language'}
          </h2>
          <p className="login-subtitle">
            {selectedLanguage 
              ? 'Preparing your personalized AI experience...' 
              : 'Select your preferred language to begin your AI-powered journey'
            }
          </p>

          {/* Language Grid */}
          {!selectedLanguage && (
            <div className="language-grid-container">
              <div className="language-grid">
                {languages.map((language) => (
                  <LanguageCard
                    key={language.code}
                    language={language}
                    onSelect={handleLanguageSelect}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Language Card Component
function LanguageCard({ language, onSelect }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="language-card"
      onClick={() => onSelect(language)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isHovered ? 'rgba(59, 130, 246, 0.1)' : 'rgba(15, 23, 42, 0.8)',
        border: isHovered ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(148, 163, 184, 0.15)',
        borderRadius: '10px',
        padding: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <FlagDisplay countryCode={language.code} />
      </div>
      <div style={{ flex: 1 }}>
        <h4 style={{ 
          margin: 0, 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#f1f5f9',
          marginBottom: '1px'
        }}>
          {language.native}
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: '11px', 
          color: '#94a3b8'
        }}>
          {language.name}
        </p>
      </div>
      <div style={{ 
        color: isHovered ? '#3b82f6' : '#64748b',
        fontSize: '18px',
        fontWeight: '600',
        transition: 'color 0.2s ease'
      }}>
        →
      </div>
    </div>
  );
}

export default ProfessionalLanguagePage; 